import React from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
} from "@material-ui/core";
import HelpLayout from "../../components/HelpCenter/HelpLayout";
import SearchBar from "../../components/HelpCenter/SearchBar";
import { useHelpCategories } from "../../hooks/useHelp";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  header: {
    textAlign: "center",
    marginBottom: theme.spacing(4),
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(4),
  },
  searchWrapper: {
    marginBottom: theme.spacing(6),
  },
  categoryCard: {
    height: "100%",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[4],
    },
  },
  categoryIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(2),
  },
  categoryTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  categoryDescription: {
    color: theme.palette.text.secondary,
  },
  articleCount: {
    marginTop: theme.spacing(1),
    color: theme.palette.primary.main,
    fontWeight: 500,
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(8),
  },
}));

const HelpCenterHome = () => {
  const classes = useStyles();
  const history = useHistory();
  const { categories, loading } = useHelpCategories({
    searchParam: "",
    parentId: null,
  });

  const getCurrentLang = () => {
    const lang = i18n.language || "pt";
    return lang.toLowerCase().substring(0, 2);
  };

  const getCategoryName = (category) => {
    const lang = getCurrentLang();
    if (lang === "en") return category.nameEn;
    if (lang === "es") return category.nameEs;
    return category.namePt;
  };

  const getCategoryDescription = (category) => {
    const lang = getCurrentLang();
    if (lang === "en") return category.descriptionEn;
    if (lang === "es") return category.descriptionEs;
    return category.descriptionPt;
  };

  const handleCategoryClick = (category) => {
    history.push(`/help/${category.slug}`);
  };

  if (loading) {
    return (
      <HelpLayout>
        <Box className={classes.loading}>
          <CircularProgress />
        </Box>
      </HelpLayout>
    );
  }

  const totalArticles = categories.reduce(
    (sum, cat) => sum + (cat.articles?.length || 0),
    0
  );

  return (
    <HelpLayout>
      <Box className={classes.header}>
        <Typography variant="h3" className={classes.title}>
          {i18n.t("helpCenter.welcome")}
        </Typography>
        <Typography variant="h6" className={classes.subtitle}>
          {i18n.t("helpCenter.subtitle")}
        </Typography>

        <Box className={classes.searchWrapper}>
          <SearchBar />
        </Box>
      </Box>

      <Grid container spacing={3}>
        {categories.map((category) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={category.id}>
            <Card className={classes.categoryCard}>
              <CardActionArea onClick={() => handleCategoryClick(category)}>
                <CardContent>
                  <Box textAlign="center">
                    <Typography
                      className={classes.categoryIcon}
                    >
                      {category.icon}
                    </Typography>
                    <Typography variant="h6" className={classes.categoryTitle}>
                      {getCategoryName(category)}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={classes.categoryDescription}
                    >
                      {getCategoryDescription(category)}
                    </Typography>
                    {category.articles && category.articles.length > 0 && (
                      <Typography
                        variant="caption"
                        className={classes.articleCount}
                      >
                        {category.articles.length}{" "}
                        {category.articles.length === 1
                          ? i18n.t("helpCenter.article")
                          : i18n.t("helpCenter.articles")}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {totalArticles > 0 && (
        <Box mt={4} textAlign="center">
          <Typography variant="body2" color="textSecondary">
            {totalArticles} {i18n.t("helpCenter.articlesAvailable")}
          </Typography>
        </Box>
      )}
    </HelpLayout>
  );
};

export default HelpCenterHome;
